import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  MapPin, 
  Globe, 
  Clock, 
  Monitor, 
  Smartphone,
  Download,
  Search,
  Filter,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  Database,
  Shield,
  FileText,
  Server,
  Cpu,
  Activity,
  ChevronDown,
  ChevronUp,
  Phone,
  Laptop,
  ExternalLink,
  Info,
  Menu,
  X,
  MoreVertical,
  Calendar,
  MessageSquare,
  Type,
  HardDrive,
  FolderTree,
  Layers,
  LogOut,
  LogIn
} from 'lucide-react';

// firebase helpers used throughout this report
import { database, ref, remove, get, set, auth } from '../../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';

const AdminLoginReport = () => {
  const [loginLogs, setLoginLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);
  const [dateFolders, setDateFolders] = useState([]);
  const [selectedDateFolder, setSelectedDateFolder] = useState('all');
  const [debugInfo, setDebugInfo] = useState({
    rawData: null,
    fetchAttempts: [],
    structure: null
  });
  const [showDebug, setShowDebug] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);
  
  // Admin login form state
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  
  const [stats, setStats] = useState({
    totalLogins: 0,
    todayLogins: 0,
    uniqueIPs: 0,
    mobileLogins: 0,
    desktopLogins: 0,
    failedLogins: 0,
    adminActions: 0
  });
  const [actionMessage, setActionMessage] = useState('');

  const [isMobile, setIsMobile] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Function to restore Firebase auth from localStorage
  const restoreFirebaseAuth = async () => {
    try {
      // Check if we have admin credentials in localStorage
      const aerovoltAuth = localStorage.getItem('aerovoltAuth');
      if (aerovoltAuth) {
        const authData = JSON.parse(aerovoltAuth);
        console.log('Found aerovoltAuth in localStorage:', authData);
        
        // If we have email and password stored, try to sign in
        if (authData.email && authData.password) {
          try {
            console.log('Attempting to restore Firebase auth with stored credentials');
            const userCredential = await signInWithEmailAndPassword(auth, authData.email, authData.password);
            console.log('Firebase auth restored successfully:', userCredential.user.uid);
            return true;
          } catch (signInError) {
            console.error('Failed to restore Firebase auth:', signInError);
            
            // If sign-in fails, try to get the current user (might already be signed in)
            if (auth.currentUser) {
              console.log('Using existing Firebase auth user:', auth.currentUser.uid);
              return true;
            }
            
            // Clear stored password for security
            localStorage.setItem('aerovoltAuth', JSON.stringify({
              ...authData,
              password: '' // Remove password
            }));
          }
        }
      }
      
      // Check if Firebase already has a user
      if (auth.currentUser) {
        console.log('Firebase already has authenticated user:', auth.currentUser.uid);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error restoring Firebase auth:', error);
      return false;
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      setAuthChecking(true);
      
      // First try to restore from localStorage
      const restored = await restoreFirebaseAuth();
      
      // Set up auth state listener
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          console.log('User is authenticated via Firebase:', user.uid, user.email);
          setIsAuthenticated(true);
          setCurrentUser(user);
          
          // Check if user has admin role in localStorage
          const aerovoltAuth = localStorage.getItem('aerovoltAuth');
          if (aerovoltAuth) {
            const authData = JSON.parse(aerovoltAuth);
            if (authData.role === 'admin') {
              console.log('User has admin role from localStorage');
              await fetchAllLoginLogs();
            } else {
              setError('You are logged in but do not have admin privileges');
              setLoading(false);
            }
          } else {
            // Try to fetch anyway - Firebase rules will determine access
            await fetchAllLoginLogs();
          }
        } else {
          console.log('User is not authenticated with Firebase');
          setIsAuthenticated(false);
          setCurrentUser(null);
          
          // Check if we have admin in localStorage but not Firebase
          const aerovoltAuth = localStorage.getItem('aerovoltAuth');
          if (aerovoltAuth) {
            const authData = JSON.parse(aerovoltAuth);
            if (authData.role === 'admin') {
              console.log('Admin found in localStorage but not in Firebase');
              setError('Please log in with Firebase to view login logs');
            } else {
              setError('Please log in as admin to view login logs');
            }
          } else {
            setError('Please log in as admin to view login logs');
          }
          setLoading(false);
        }
        setAuthChecking(false);
      });

      return () => unsubscribe();
    };

    checkAuth();
  }, []);

  // Handle admin login
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);
    
    try {
      console.log('Attempting admin login with:', adminEmail);
      const userCredential = await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
      console.log('Admin login successful:', userCredential.user.uid);
      
      // Update localStorage with admin info
      localStorage.setItem('aerovoltAuth', JSON.stringify({
        email: adminEmail,
        password: adminPassword, // Store password for session restoration
        role: 'admin',
        uid: userCredential.user.uid,
        timestamp: new Date().toISOString()
      }));
      
      // Clear login form
      setShowLoginForm(false);
      setAdminEmail('');
      setAdminPassword('');
      
      // Fetch logs
      await fetchAllLoginLogs();
    } catch (error) {
      console.error('Admin login error:', error);
      setLoginError(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoggingIn(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await auth.signOut();
      // Clear stored password from localStorage
      const aerovoltAuth = localStorage.getItem('aerovoltAuth');
      if (aerovoltAuth) {
        const authData = JSON.parse(aerovoltAuth);
        localStorage.setItem('aerovoltAuth', JSON.stringify({
          ...authData,
          password: '' // Remove password on logout
        }));
      }
      setIsAuthenticated(false);
      setCurrentUser(null);
      setLoginLogs([]);
      setFilteredLogs([]);
      setError('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Fetch all logs from all date folders using Firebase SDK
  const fetchAllLoginLogs = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Check if user is authenticated
      if (!auth.currentUser) {
        setError('Please log in as admin to view login logs');
        setLoading(false);
        return;
      }
      
      console.log('Fetching admin data as user:', auth.currentUser.uid);
      
      const fetchAttempt = {
        timestamp: new Date().toISOString(),
        url: 'admin',
        status: 'pending'
      };
      
      // Use Firebase SDK get() instead of fetch - this automatically handles authentication
      const adminRef = ref(database, 'admin');
      const snapshot = await get(adminRef);
      
      fetchAttempt.status = 'success';
      fetchAttempt.statusCode = 200;
      
      if (snapshot.exists()) {
        const adminData = snapshot.val();
        
        // Store debug info
        setDebugInfo(prev => ({
          ...prev,
          rawData: adminData,
          fetchAttempts: [...prev.fetchAttempts, fetchAttempt],
          structure: adminData ? Object.keys(adminData) : []
        }));
        
        // Get all date folders (keys that look like dates)
        const folders = Object.keys(adminData).filter(key => 
          /\d{4}-\d{2}-\d{2}/.test(key)
        );
        
        setDateFolders(folders);
        
        let allLogs = [];
        
        // Loop through all date folders
        for (const folder of folders) {
          // Check if this date folder has logs
          const folderData = adminData[folder];
          
          if (folderData) {
            // Based on your screenshot, logs might be stored in different structures
            // Let's try multiple possible paths where logs could be stored
            
            // Path 1: Direct logs object at the folder level
            if (folderData.logs) {
              const folderLogs = folderData.logs;
              
              // Logs could be stored as an object with numeric keys or as an array
              if (folderLogs && typeof folderLogs === 'object') {
                const dateLogs = Object.entries(folderLogs).map(([logKey, logValue]) => {
                  // Ensure we have a consistent log object structure
                  const logObj = typeof logValue === 'object' ? logValue : { message: logValue };
                  
                  return {
                    id: `${folder}_${logKey}`,
                    dateFolder: folder,
                    loginTime: logObj.loginTime || logObj.timestamp || new Date().toISOString(),
                    location: logObj.location || {},
                    ipAddress: logObj.ipAddress || logObj.location?.ipAddress || 'Unknown',
                    deviceType: logObj.deviceType || 'Desktop',
                    userAgent: logObj.userAgent || '',
                    userId: logObj.userId || 'admin',
                    userName: logObj.userName || 'Administrator',
                    firebaseUid: logObj.firebaseUid || auth.currentUser?.uid,
                    status: logObj.status || 'success',
                    type: logObj.type || '',
                    message: logObj.message || ''
                  };
                });
                
                allLogs = [...allLogs, ...dateLogs];
              }
            }
            
            // Path 2: Logs inside adminAccount
            if (folderData.adminAccount && folderData.adminAccount.logs) {
              const adminAccountLogs = folderData.adminAccount.logs;
              
              if (adminAccountLogs && typeof adminAccountLogs === 'object') {
                const accountLogs = Object.entries(adminAccountLogs).map(([logKey, logValue]) => {
                  const logObj = typeof logValue === 'object' ? logValue : { message: logValue };
                  
                  return {
                    id: `${folder}_admin_${logKey}`,
                    dateFolder: folder,
                    loginTime: logObj.loginTime || logObj.timestamp || new Date().toISOString(),
                    location: logObj.location || {},
                    ipAddress: logObj.ipAddress || logObj.location?.ipAddress || 'Admin Dashboard',
                    deviceType: logObj.deviceType || 'Desktop',
                    userAgent: logObj.userAgent || '',
                    userId: logObj.userId || 'admin',
                    userName: logObj.userName || 'Administrator',
                    firebaseUid: logObj.firebaseUid || auth.currentUser?.uid,
                    status: logObj.status || 'success',
                    type: logObj.type || logObj.action || 'admin_action',
                    message: logObj.message || logObj.details || ''
                  };
                });
                
                allLogs = [...allLogs, ...accountLogs];
              }
            }
            
            // Path 3: Direct log entries at folder level (like in your screenshot)
            // If the folder itself contains log entries with numeric keys
            const possibleLogKeys = Object.keys(folderData).filter(key => 
              !isNaN(parseInt(key)) || key === 'logs' || key === 'adminAccount'
            );
            
            for (const key of possibleLogKeys) {
              if (key !== 'logs' && key !== 'adminAccount' && folderData[key] && typeof folderData[key] === 'object') {
                const logObj = folderData[key];
                
                allLogs.push({
                  id: `${folder}_${key}`,
                  dateFolder: folder,
                  loginTime: logObj.loginTime || logObj.timestamp || new Date().toISOString(),
                  location: logObj.location || {},
                  ipAddress: logObj.ipAddress || logObj.location?.ipAddress || 'Unknown',
                  deviceType: logObj.deviceType || 'Desktop',
                  userAgent: logObj.userAgent || '',
                  userId: logObj.userId || 'admin',
                  userName: logObj.userName || 'Administrator',
                  firebaseUid: logObj.firebaseUid || auth.currentUser?.uid,
                  status: logObj.status || 'success',
                  type: logObj.type || '',
                  message: logObj.message || ''
                });
              }
            }
          }
        }
        
        if (allLogs.length > 0) {
          // Sort by timestamp (newest first)
          allLogs.sort((a, b) => {
            const dateA = new Date(a.loginTime || 0);
            const dateB = new Date(b.loginTime || 0);
            return dateB - dateA;
          });
          
          setLoginLogs(allLogs);
          setFilteredLogs(allLogs);
          calculateStats(allLogs);
          setError(''); // Clear any previous errors
        } else {
          setLoginLogs([]);
          setFilteredLogs([]);
          if (folders.length > 0) {
            // Log the structure to help debug
            console.log('Admin data structure:', adminData);
            setError(`Found ${folders.length} date folders but no login logs found. Check console for data structure.`);
          } else {
            setError('No date folders found in admin data');
          }
        }
      } else {
        setLoginLogs([]);
        setFilteredLogs([]);
        setError('Admin data is empty');
      }
    } catch (err) {
      console.error('Error fetching login logs:', err);
      
      // Check for permission denied error
      if (err.code === 'PERMISSION_DENIED') {
        setError('Permission denied. You need admin privileges to view these logs.');
      } else {
        setError(`Failed to load admin login logs: ${err.message}`);
      }
      
      setDebugInfo(prev => ({
        ...prev,
        fetchAttempts: [...prev.fetchAttempts, {
          timestamp: new Date().toISOString(),
          url: 'admin',
          status: 'error',
          error: err.message
        }]
      }));
    } finally {
      setLoading(false);
    }
  };

  // logout every other device / clear all sessions
  const logoutAllDevices = async () => {
    // Check authentication first
    if (!auth.currentUser) {
      setActionMessage('❌ You must be logged in as admin to perform this action.');
      return;
    }

    const ok = window.confirm('Are you sure you want to log out all admin sessions across all dates?');
    if (!ok) return;

    try {
      setActionMessage('🔄 Clearing all admin sessions...');
      
      // remove existing sessions under every date folder
      const adminRootRef = ref(database, 'admin');
      const rootSnap = await get(adminRootRef);
      if (rootSnap.exists()) {
        const adminData = rootSnap.val();
        for (const dateKey of Object.keys(adminData)) {
          // remove sessions
          await remove(ref(database, `admin/${dateKey}/sessions`));
          // also clear logs for that date when user requests it
          await remove(ref(database, `admin/${dateKey}/logs`));
        }
      }

      // after clearing, create a fresh "marker" session entry in each folder
      if (rootSnap.exists()) {
        const adminData = rootSnap.val();
        const now = new Date().toISOString();
        for (const dateKey of Object.keys(adminData)) {
          const sessionId = `logout_${Date.now()}`;
          const sessionRef = ref(database, `admin/${dateKey}/sessions/${sessionId}`);
          await set(sessionRef, {
            sessionId,
            createdAt: now,
            type: 'logout_marker',
            initiatedBy: 'Admin Login Report'
          });
        }
      }

      setActionMessage('✅ All admin sessions cleared across all dates (marker entries created).');

      // record this as an admin action in each log folder
      try {
        if (rootSnap.exists()) {
          const adminData = rootSnap.val();
          for (const dateKey of Object.keys(adminData)) {
            const logsRef = ref(database, `admin/${dateKey}/logs`);
            const snap = await get(logsRef);
            let existing = [];
            if (snap.exists()) {
              const logs = snap.val();
              if (Array.isArray(logs)) {
                existing = logs;
              } else if (typeof logs === 'object') {
                existing = Object.values(logs);
              }
            }
            
            // Generate next log key
            const logKey = existing.length.toString();
            const newLog = {
              type: 'admin_action',
              userId: 'admin',
              loginTime: new Date().toISOString(),
              ipAddress: 'Admin Dashboard',
              status: 'success',
              message: 'Cleared all sessions across dates'
            };
            
            await set(ref(database, `admin/${dateKey}/logs/${logKey}`), newLog);
          }
        }
      } catch (logErr) {
        console.warn('failed to record admin action:', logErr);
      }

      // refresh view so session count updates if shown
      fetchAllLoginLogs();
    } catch (err) {
      console.error('Logout all devices error', err);
      setActionMessage('❌ Failed to clear sessions.');
    }
  };

  const calculateStats = (logs) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todayLogins = logs.filter(log => {
      const logDate = new Date(log.loginTime || log.timestamp);
      return logDate >= today;
    }).length;

    const uniqueIPs = new Set(logs.map(log => 
      log.location?.ipAddress || log.ipAddress || 'Unknown'
    )).size;

    const mobileLogins = logs.filter(log => 
      log.deviceType === 'Mobile'
    ).length;

    const desktopLogins = logs.filter(log => 
      log.deviceType === 'Desktop'
    ).length;

    const failedLogins = logs.filter(log => 
      log.status === 'failed'
    ).length;

    const adminActions = logs.filter(log => 
      log.ipAddress === 'Admin Dashboard' || log.type === 'password_change' || log.type === 'admin_action'
    ).length;

    setStats({
      totalLogins: logs.length,
      todayLogins,
      uniqueIPs,
      mobileLogins,
      desktopLogins,
      failedLogins,
      adminActions
    });
  };

  useEffect(() => {
    let filtered = [...loginLogs];

    // Filter by date folder if selected
    if (selectedDateFolder !== 'all') {
      filtered = filtered.filter(log => log.dateFolder === selectedDateFolder);
    }

    const now = new Date();
    if (filter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.loginTime || log.timestamp);
        return logDate >= today;
      });
    } else if (filter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.loginTime || log.timestamp);
        return logDate >= weekAgo;
      });
    } else if (filter === 'month') {
      const monthAgo = new Date();
      monthAgo.setDate(monthAgo.getDate() - 30);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.loginTime || log.timestamp);
        return logDate >= monthAgo;
      });
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        (log.location?.ipAddress && log.location.ipAddress.toLowerCase().includes(term)) ||
        (log.ipAddress && log.ipAddress.toLowerCase().includes(term)) ||
        (log.location?.city && log.location.city.toLowerCase().includes(term)) ||
        (log.location?.country && log.location.country.toLowerCase().includes(term)) ||
        (log.deviceType && log.deviceType.toLowerCase().includes(term)) ||
        (log.userAgent && log.userAgent.toLowerCase().includes(term)) ||
        (log.userId && log.userId.toLowerCase().includes(term)) ||
        (log.userName && log.userName.toLowerCase().includes(term)) ||
        (log.type && log.type.toLowerCase().includes(term)) ||
        (log.message && log.message.toLowerCase().includes(term)) ||
        (log.dateFolder && log.dateFolder.toLowerCase().includes(term))
      );
    }

    setFilteredLogs(filtered);
  }, [loginLogs, filter, searchTerm, selectedDateFolder]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getLocationString = (log) => {
    if (!log.location) return 'Unknown Location';
    
    const loc = log.location;
    let locationStr = '';
    
    if (loc.city && loc.city !== 'Unknown') locationStr += loc.city;
    if (loc.region && loc.region !== 'Unknown') {
      if (locationStr) locationStr += ', ';
      locationStr += loc.region;
    }
    if (loc.country && loc.country !== 'Unknown') {
      if (locationStr) locationStr += ', ';
      locationStr += loc.country;
    }
    
    return locationStr || 'Unknown Location';
  };

  // Check if log is an admin action (password change, etc.)
  const isAdminActionLog = (log) => {
    return log.ipAddress === 'Admin Dashboard' || log.type === 'password_change' || log.type === 'admin_action';
  };

  const toggleAccordion = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'IP Address', 'Location', 'Device', 'User Agent', 'Status', 'User ID', 'Type', 'Message', 'Date Folder'];
    const csvRows = [
      headers.join(','),
      ...filteredLogs.map(log => [
        formatDate(log.loginTime || log.timestamp),
        log.location?.ipAddress || log.ipAddress || 'Unknown',
        getLocationString(log),
        log.deviceType || 'Unknown',
        `"${(log.userAgent || '').replace(/"/g, '""')}"`,
        log.status || 'success',
        log.userId || 'admin',
        log.type || '',
        `"${(log.message || '').replace(/"/g, '""')}"`,
        log.dateFolder || 'Unknown'
      ].join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin_login_logs_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    return status === 'failed' ? 'text-red-400 bg-red-500/10' : 'text-green-400 bg-green-500/10';
  };

  const getStatusIcon = (status) => {
    return status === 'failed' ? XCircle : CheckCircle;
  };

  // Get appropriate icon for log type
  const getLogTypeIcon = (log) => {
    if (isAdminActionLog(log)) {
      return Shield;
    }
    return log.status === 'failed' ? XCircle : CheckCircle;
  };

  // Manual retry function
  const retryFetch = () => {
    fetchAllLoginLogs();
  };

  // Add debug function to inspect data structure
  const inspectDataStructure = async () => {
    try {
      // Check if user is authenticated
      if (!auth.currentUser) {
        setError('Please log in first to inspect data');
        setShowLoginForm(true);
        return;
      }
      
      const adminRef = ref(database, 'admin');
      const snapshot = await get(adminRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log('Full admin data structure:', JSON.stringify(data, null, 2));
        alert('Check console for full data structure (F12)');
      } else {
        console.log('No admin data found');
        alert('No admin data found in Firebase');
      }
    } catch (err) {
      console.error('Error inspecting data:', err);
      alert('Error inspecting data: ' + err.message);
    }
  };

  if (authChecking) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mb-4"></div>
        <div className="text-silver font-heading">Checking authentication...</div>
      </div>
    );
  }

  return (
    <div className="container-custom">
      <div className="mb-8">
       
        {/* Compact Mobile Header */}
        <div className="mb-6">
          {/* Mobile Header Top Row */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground font-heading leading-tight">
                  Admin Login Report
                </h1>
                <p className="text-xs text-silver font-body hidden sm:block">
                  Complete login history from all dates
                </p>
              </div>
            </div>
            
            {/* Mobile Action Buttons */}
            <div className="flex items-center gap-2">
              {!isAuthenticated ? (
                <button
                  onClick={() => setShowLoginForm(true)}
                  className="p-2 bg-primary/10 hover:bg-primary/20 rounded-lg text-primary transition-colors"
                  title="Login"
                >
                  <LogIn className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={retryFetch}
                  className="p-2 bg-primary/10 hover:bg-primary/20 rounded-lg text-primary transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="p-2 bg-secondary hover:bg-secondary/80 rounded-lg text-primary transition-colors md:hidden"
                title="Show Filters"
              >
                {showMobileFilters ? <X className="h-4 w-4" /> : <Filter className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Mobile Description - Only show on very small screens */}
          <div className="sm:hidden mb-4">
            <p className="text-xs text-silver font-body">
              Complete login history from all dates
            </p>
          </div>

          {/* Desktop Header - Full Width */}
          <div className="hidden md:flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="icon-electric">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground font-heading">Admin Login Report</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <Layers className="h-3 w-3 text-primary" />
                    <code className="text-xs text-silver font-mono font-body">
                      {isAuthenticated 
                        ? (dateFolders.length > 0 
                          ? `Complete history from ${dateFolders.length} date folder${dateFolders.length !== 1 ? 's' : ''}`
                          : 'No date folders found')
                        : 'Authentication required'}
                    </code>
                  </div>
                  {currentUser && (
                    <div className="text-xs text-silver-light mt-1">
                      Logged in as: {currentUser.email}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={inspectDataStructure}
                className="btn-electric flex items-center px-4 py-2 rounded-xl font-medium bg-blue-500/10 hover:bg-blue-500/20"
                title="Inspect data structure in console"
              >
                <Database className="h-4 w-4 mr-2" />
                Inspect Data
              </button>
              {isAuthenticated ? (
                <>
                  <button
                    onClick={retryFetch}
                    className="btn-electric flex items-center px-4 py-2 rounded-xl font-medium"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh All
                  </button>
                  <button
                    onClick={exportToCSV}
                    className="btn-electric flex items-center px-4 py-2 rounded-xl font-medium"
                    disabled={filteredLogs.length === 0}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </button>
                  <button
                    onClick={logoutAllDevices}
                    className="btn-electric flex items-center px-4 py-2 rounded-xl font-medium bg-red-500/10 hover:bg-red-500/20"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout All Devices
                  </button>
                  <button
                    onClick={handleLogout}
                    className="btn-electric flex items-center px-4 py-2 rounded-xl font-medium bg-orange-500/10 hover:bg-orange-500/20"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowLoginForm(true)}
                  className="btn-electric flex items-center px-4 py-2 rounded-xl font-medium bg-green-500/10 hover:bg-green-500/20"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Admin Login
                </button>
              )}
            </div>
          </div>

          {/* display message from admin actions */}
          {actionMessage && (
            <div className="mb-4 p-3 rounded-lg bg-green-900/30 text-green-300 font-body text-sm">
              {actionMessage}
            </div>
          )}

          {/* Admin Login Form Modal */}
          {showLoginForm && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
              <div className="glass-card w-full max-w-md">
                <div className="p-4 border-b border-border flex justify-between items-center">
                  <h3 className="text-lg font-bold text-foreground font-heading">Admin Login</h3>
                  <button
                    onClick={() => setShowLoginForm(false)}
                    className="text-silver hover:text-foreground transition-colors p-1"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
                
                <form onSubmit={handleAdminLogin} className="p-4 space-y-4">
                  {loginError && (
                    <div className="p-3 bg-red-900/30 border border-red-600/30 rounded-lg text-red-300 text-sm">
                      {loginError}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-silver font-body text-sm mb-1">Email</label>
                    <input
                      type="email"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors font-body"
                      placeholder="admin@example.com"
                      required
                      disabled={loggingIn}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-silver font-body text-sm mb-1">Password</label>
                    <input
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      className="w-full px-3 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors font-body"
                      placeholder="••••••••"
                      required
                      disabled={loggingIn}
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loggingIn}
                    className="w-full px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg font-body transition-colors disabled:opacity-50"
                  >
                    {loggingIn ? 'Logging in...' : 'Login'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Mobile Filters Panel */}
          {showMobileFilters && isMobile && (
            <div className="mb-4 p-4 bg-secondary rounded-lg border border-border">
              <div className="space-y-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors font-body text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-silver" />
                </div>
                
                <div>
                  <label className="text-xs text-silver font-body mb-1 block">Date Folder</label>
                  <select
                    value={selectedDateFolder}
                    onChange={(e) => setSelectedDateFolder(e.target.value)}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-primary transition-colors font-body text-sm"
                  >
                    <option value="all">All Dates ({loginLogs.length} logs)</option>
                    {dateFolders.map(folder => {
                      const count = loginLogs.filter(l => l.dateFolder === folder).length;
                      return (
                        <option key={folder} value={folder}>
                          {folder} ({count} logs)
                        </option>
                      );
                    })}
                  </select>
                </div>
                
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-primary flex-shrink-0" />
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-primary transition-colors font-body text-sm"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
                
                {isAuthenticated && (
                  <button
                    onClick={exportToCSV}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors text-sm font-body"
                    disabled={filteredLogs.length === 0}
                  >
                    <Download className="h-4 w-4" />
                    Export Current View
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-600/30 rounded-xl card-glow">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" />
              <span className="text-red-300 font-body text-sm flex-1">{error}</span>
              {error.includes('log in as admin') && (
                <button
                  onClick={() => setShowLoginForm(true)}
                  className="ml-4 px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-xs transition-colors"
                >
                  Admin Login
                </button>
              )}
              {error.includes('no login logs') && isAuthenticated && (
                <button
                  onClick={inspectDataStructure}
                  className="ml-4 px-3 py-1 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg text-xs transition-colors"
                >
                  Debug
                </button>
              )}
            </div>
          </div>
        )}

        {/* Statistics Cards - Compact on Mobile */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-8">
          <div className="card-glow p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-silver font-body">Total Logs</p>
                <p className="text-xl font-bold text-foreground font-heading">{stats.totalLogins}</p>
              </div>
              <Database className="h-6 w-6 text-primary" />
            </div>
          </div>
          
          <div className="card-glow p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-silver font-body">Today's</p>
                <p className="text-xl font-bold text-foreground font-heading">{stats.todayLogins}</p>
              </div>
              <Clock className="h-6 w-6 text-primary" />
            </div>
          </div>
          
          <div className="card-glow p-3 hidden sm:block">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-silver font-body">Unique IPs</p>
                <p className="text-xl font-bold text-foreground font-heading">{stats.uniqueIPs}</p>
              </div>
              <Globe className="h-6 w-6 text-primary" />
            </div>
          </div>
          
          <div className="card-glow p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-silver font-body">Mobile</p>
                <p className="text-xl font-bold text-foreground font-heading">{stats.mobileLogins}</p>
              </div>
              <Smartphone className="h-6 w-6 text-primary" />
            </div>
          </div>
          
          <div className="card-glow p-3 hidden lg:block">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-silver font-body">Desktop</p>
                <p className="text-xl font-bold text-foreground font-heading">{stats.desktopLogins}</p>
              </div>
              <Monitor className="h-6 w-6 text-primary" />
            </div>
          </div>

          <div className="card-glow p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-silver font-body">Failed</p>
                <p className="text-xl font-bold text-foreground font-heading">{stats.failedLogins}</p>
              </div>
              <AlertCircle className="h-6 w-6 text-red-400" />
            </div>
          </div>

          <div className="card-glow p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-silver font-body">Admin Actions</p>
                <p className="text-xl font-bold text-foreground font-heading">{stats.adminActions}</p>
              </div>
              <Shield className="h-6 w-6 text-primary" />
            </div>
          </div>
        </div>

        {/* Desktop Filters and Search */}
        <div className="hidden md:block card-glow p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by IP, city, country, device, type, message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors font-body"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-silver" />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <FolderTree className="h-4 w-4 text-primary" />
                <select
                  value={selectedDateFolder}
                  onChange={(e) => setSelectedDateFolder(e.target.value)}
                  className="bg-secondary border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-primary transition-colors font-body text-sm min-w-[200px]"
                >
                  <option value="all">All Date Folders ({loginLogs.length} logs)</option>
                  {dateFolders.map(folder => {
                    const count = loginLogs.filter(l => l.dateFolder === folder).length;
                    return (
                      <option key={folder} value={folder}>
                        {folder} ({count} logs)
                      </option>
                    );
                  })}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-primary" />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="bg-secondary border border-border rounded-lg px-3 py-2 text-foreground focus:outline-none focus:border-primary transition-colors font-body text-sm"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar - Always visible */}
        <div className="md:hidden mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search login logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-secondary border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors font-body text-sm"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-silver" />
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary"
            >
              <Filter className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Login Logs */}
        <div className="card-glow overflow-hidden">
          {!isAuthenticated ? (
            <div className="p-8 text-center">
              <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2 font-heading">Authentication Required</h3>
              <p className="text-silver font-body mb-4">Please log in as admin to view login logs.</p>
              <button
                onClick={() => setShowLoginForm(true)}
                className="px-6 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors font-body"
              >
                Admin Login
              </button>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2 font-heading">No Login Logs Found</h3>
              <p className="text-silver font-body">No admin login activity has been recorded yet.</p>
              <div className="mt-4 text-xs text-silver-light font-body">
                <div className="flex items-center justify-center gap-1">
                  <FolderTree className="h-3 w-3" />
                  Checked {dateFolders.length} date folder{dateFolders.length !== 1 ? 's' : ''}
                </div>
                {dateFolders.length > 0 && (
                  <div className="mt-2 space-y-2">
                    <p className="text-xs">Try clicking the "Inspect Data" button above to see the structure in console.</p>
                    <button
                      onClick={retryFetch}
                      className="text-primary hover:underline text-xs"
                    >
                      Click here to refresh
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-secondary/80">
                      <th className="py-3 px-4 text-left text-silver font-medium font-heading">Timestamp</th>
                      <th className="py-3 px-4 text-left text-silver font-medium font-heading">IP Address/Type</th>
                      <th className="py-3 px-4 text-left text-silver font-medium font-heading">Location</th>
                      <th className="py-3 px-4 text-left text-silver font-medium font-heading">Device</th>
                      <th className="py-3 px-4 text-left text-silver font-medium font-heading">User ID</th>
                      <th className="py-3 px-4 text-left text-silver font-medium font-heading">Status/Message</th>
                      <th className="py-3 px-4 text-left text-silver font-medium font-heading">Date Folder</th>
                      <th className="py-3 px-4 text-left text-silver font-medium font-heading">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => {
                      const isAdminAction = isAdminActionLog(log);
                      const LogTypeIcon = getLogTypeIcon(log);
                      
                      return (
                        <tr key={log.id} className="border-t border-border/30 hover:bg-secondary/20 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-primary" />
                              <span className="text-foreground text-sm font-body">{formatDate(log.loginTime || log.timestamp)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <LogTypeIcon className={`h-4 w-4 ${isAdminAction ? 'text-blue-400' : log.status === 'failed' ? 'text-red-400' : 'text-green-400'}`} />
                              <div>
                                <div className="font-mono text-foreground font-medium font-body">
                                  {isAdminAction ? 'Admin Dashboard' : log.location?.ipAddress || log.ipAddress || 'Unknown IP'}
                                </div>
                                {log.type && (
                                  <div className="text-xs text-blue-400 font-body">
                                    Type: {log.type}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-start gap-2">
                              <MapPin className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-foreground font-body">{getLocationString(log)}</div>
                                {!isAdminAction && (
                                  <div className="text-xs text-silver-light font-body">
                                    {log.location?.locationSource || 'Source: Unknown'}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {log.deviceType === 'Mobile' ? (
                                <Smartphone className="h-5 w-5 text-primary" />
                              ) : (
                                <Monitor className="h-5 w-5 text-primary" />
                              )}
                              <span className="text-foreground font-body">{log.deviceType || 'Unknown'}</span>
                            </div>
                            <div className="text-xs text-silver-light truncate max-w-xs font-body">
                              {log.userAgent ? log.userAgent.substring(0, 50) + '...' : 'No user agent'}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="text-foreground font-body">
                              {log.userId || 'admin'}
                            </div>
                            <div className="text-xs text-silver-light font-body">
                              {log.userName || 'Administrator'}
                            </div>
                            {log.firebaseUid && (
                              <div className="text-xs text-silver-light font-body truncate max-w-xs">
                                UID: {log.firebaseUid}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {isAdminAction ? (
                                <>
                                  <Shield className="h-4 w-4 text-blue-400" />
                                  <div>
                                    <div className="text-blue-300 text-sm font-body">Admin Action</div>
                                    {log.message && (
                                      <div className="text-xs text-silver-light font-body truncate max-w-xs">
                                        {log.message}
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : log.status === 'failed' ? (
                                <>
                                  <XCircle className="h-4 w-4 text-red-400" />
                                  <span className="text-red-300 text-sm font-body">Failed</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 text-green-400" />
                                  <span className="text-green-300 text-sm font-body">Success</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 text-primary" />
                              <span className="text-xs text-silver-light font-body">{log.dateFolder || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => setSelectedLog(log)}
                              className="p-1.5 hover:bg-primary/10 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4 text-primary" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Accordion View */}
              <div className="md:hidden">
                <div className="p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-silver font-heading">
                      Login Records ({filteredLogs.length})
                    </h3>
                    <button
                      onClick={exportToCSV}
                      className="text-xs flex items-center gap-1 text-primary font-body"
                      disabled={filteredLogs.length === 0}
                    >
                      <Download className="h-3 w-3" />
                      Export
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    {filteredLogs.map((log) => {
                      const StatusIcon = getStatusIcon(log.status);
                      const isAdminAction = isAdminActionLog(log);
                      const LogTypeIcon = getLogTypeIcon(log);
                      
                      return (
                        <div key={log.id} className="border border-border rounded-lg overflow-hidden bg-secondary/30">
                          {/* Accordion Header */}
                          <button
                            onClick={() => toggleAccordion(log.id)}
                            className="w-full p-3 flex items-center justify-between hover:bg-secondary/50 transition-colors"
                          >
                            <div className="flex items-center gap-3 flex-1">
                              <div className={`p-1.5 rounded ${isAdminAction ? 'bg-blue-500/10' : getStatusColor(log.status)}`}>
                                <LogTypeIcon className={`h-3 w-3 ${isAdminAction ? 'text-blue-400' : log.status === 'failed' ? 'text-red-400' : 'text-green-400'}`} />
                              </div>
                              <div className="text-left">
                                <div className="font-medium text-foreground font-heading text-xs flex items-center gap-1">
                                  {isAdminAction ? (
                                    <Shield className="h-3 w-3 text-blue-400" />
                                  ) : log.deviceType === 'Mobile' ? (
                                    <Smartphone className="h-3 w-3" />
                                  ) : (
                                    <Monitor className="h-3 w-3" />
                                  )}
                                  {isAdminAction ? 'Admin Action' : log.deviceType || 'Unknown Device'}
                                </div>
                                <div className="text-xs text-silver font-body truncate max-w-[180px]">
                                  {formatDate(log.loginTime || log.timestamp).split(',')[0]}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-[10px] text-silver-light">
                                {log.dateFolder}
                              </div>
                              {expandedLog === log.id ? (
                                <ChevronUp className="h-3 w-3 text-primary" />
                              ) : (
                                <ChevronDown className="h-3 w-3 text-primary" />
                              )}
                            </div>
                          </button>

                          {/* Accordion Content */}
                          {expandedLog === log.id && (
                            <div className="p-3 border-t border-border bg-secondary/20 space-y-3">
                              {/* IP Address or Type */}
                              <div className="flex items-start gap-2">
                                <Server className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="text-[10px] text-silver font-body mb-0.5">
                                    {isAdminAction ? 'Action Type' : 'IP Address'}
                                  </div>
                                  <div className="font-mono text-foreground font-medium text-xs break-all font-body">
                                    {isAdminAction ? (log.type || 'Admin Action') : log.location?.ipAddress || log.ipAddress || 'Unknown'}
                                  </div>
                                </div>
                              </div>

                              {/* Message (for admin actions) */}
                              {log.message && (
                                <div className="flex items-start gap-2">
                                  <MessageSquare className="h-3 w-3 text-blue-400 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <div className="text-[10px] text-silver font-body mb-0.5">Message</div>
                                    <div className="text-foreground font-medium text-xs font-body">
                                      {log.message}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Location (only for non-admin actions) */}
                              {!isAdminAction && (
                                <div className="flex items-start gap-2">
                                  <MapPin className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <div className="text-[10px] text-silver font-body mb-0.5">Location</div>
                                    <div className="text-foreground font-medium text-xs font-body">
                                      {getLocationString(log) || 'Unknown'}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* User Info */}
                              <div className="flex items-start gap-2">
                                <Shield className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="text-[10px] text-silver font-body mb-0.5">User</div>
                                  <div className="text-foreground font-medium text-xs font-body">
                                    {log.userId || 'admin'} - {log.userName || 'Administrator'}
                                  </div>
                                </div>
                              </div>

                              {/* Date Folder */}
                              <div className="flex items-start gap-2">
                                <Calendar className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="text-[10px] text-silver font-body mb-0.5">Date Folder</div>
                                  <div className="text-foreground font-medium text-xs font-body">
                                    {log.dateFolder || 'Unknown'}
                                  </div>
                                </div>
                              </div>

                              {/* View Details Button */}
                              <button
                                onClick={() => setSelectedLog(log)}
                                className="w-full flex items-center justify-center gap-1 px-2 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded text-xs font-body"
                              >
                                <Info className="h-3 w-3" />
                                View Full Details
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
          
          {filteredLogs.length > 0 && (
            <div className="px-4 py-3 border-t border-border/30 bg-secondary/20 flex flex-col sm:flex-row justify-between items-center gap-2">
              <div className="text-xs text-silver font-body">
                Showing {filteredLogs.length} of {loginLogs.length} records
              </div>
              <div className="flex items-center gap-2">
                <div className="text-xs text-silver-light flex items-center gap-1 font-body">
                  <FolderTree className="h-3 w-3" />
                  {selectedDateFolder === 'all' ? 'All folders' : selectedDateFolder}
                </div>
                <div className="text-xs text-silver-light font-body">
                  Updated: {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Log Details Modal - Mobile Optimized */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="glass-card w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full ${isAdminActionLog(selectedLog) ? 'bg-blue-500/10' : selectedLog.status === 'failed' ? 'bg-red-500/10' : 'bg-green-500/10'} flex items-center justify-center`}>
                  {isAdminActionLog(selectedLog) ? (
                    <Shield className="h-4 w-4 text-blue-400" />
                  ) : selectedLog.status === 'failed' ? (
                    <XCircle className="h-4 w-4 text-red-400" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground font-heading">
                    {isAdminActionLog(selectedLog) ? 'Admin Action Details' : 'Login Details'}
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-silver hover:text-foreground transition-colors p-1"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-silver font-heading">Timestamp</label>
                  <div className="text-foreground font-medium font-body text-sm mt-0.5">
                    {formatDate(selectedLog.loginTime || selectedLog.timestamp)}
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-silver font-heading">
                    {isAdminActionLog(selectedLog) ? 'Action Type' : 'IP Address'}
                  </label>
                  <div className="text-foreground font-mono font-medium text-sm mt-0.5 font-body">
                    {isAdminActionLog(selectedLog) ? (selectedLog.type || 'Admin Action') : selectedLog.location?.ipAddress || selectedLog.ipAddress || 'Unknown'}
                  </div>
                </div>
                
                {/* Show Type for Admin Actions */}
                {selectedLog.type && (
                  <div>
                    <label className="text-xs text-silver font-heading">Action Type</label>
                    <div className="text-foreground font-medium text-sm mt-0.5 font-body">
                      <div className="flex items-center gap-2">
                        <Type className="h-4 w-4 text-blue-400" />
                        {selectedLog.type}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Show Message for Admin Actions */}
                {selectedLog.message && (
                  <div>
                    <label className="text-xs text-silver font-heading">Message</label>
                    <div className="text-foreground font-medium text-sm mt-0.5 font-body">
                      <div className="flex items-start gap-2">
                        <MessageSquare className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <span>{selectedLog.message}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Show Location only for non-admin actions */}
                {!isAdminActionLog(selectedLog) && (
                  <div>
                    <label className="text-xs text-silver font-heading">Location</label>
                    <div className="text-foreground font-medium text-sm mt-0.5 font-body">
                      {selectedLog.location?.city || 'Unknown'}, {selectedLog.location?.country || 'Unknown'}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-xs text-silver font-heading">Device & Status</label>
                  <div className="flex items-center gap-2 mt-0.5">
                    <div className={`px-2 py-1 rounded text-xs ${selectedLog.status === 'failed' ? 'bg-red-500/10 text-red-400' : isAdminActionLog(selectedLog) ? 'bg-blue-500/10 text-blue-400' : 'bg-green-500/10 text-green-400'}`}>
                      {selectedLog.deviceType || 'Unknown'}
                    </div>
                    <div className={`px-2 py-1 rounded text-xs ${selectedLog.status === 'failed' ? 'bg-red-500/10 text-red-400' : isAdminActionLog(selectedLog) ? 'bg-blue-500/10 text-blue-400' : 'bg-green-500/10 text-green-400'}`}>
                      {isAdminActionLog(selectedLog) ? 'Admin Action' : selectedLog.status === 'failed' ? 'Failed' : 'Success'}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-silver font-heading">User Information</label>
                  <div className="text-foreground font-medium text-sm mt-0.5 font-body">
                    User ID: {selectedLog.userId || 'admin'}
                  </div>
                  <div className="text-foreground text-sm mt-0.5 font-body">
                    Name: {selectedLog.userName || 'Administrator'}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-silver font-heading">Date Folder</label>
                  <div className="text-foreground font-medium text-sm mt-0.5 font-body flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    {selectedLog.dateFolder || 'Unknown'}
                  </div>
                </div>
              </div>
              
              {/* Show User Agent for non-admin actions */}
              {!isAdminActionLog(selectedLog) && selectedLog.userAgent && (
                <div>
                  <label className="text-xs text-silver font-heading mb-2 block">User Agent</label>
                  <div className="bg-secondary/50 border border-border rounded-lg p-2">
                    <code className="text-foreground text-xs break-all font-mono font-body">
                      {selectedLog.userAgent}
                    </code>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-border">
              <button
                onClick={() => setSelectedLog(null)}
                className="w-full px-4 py-2 btn-electric rounded-lg font-body text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLoginReport;